import { useMemo } from 'react';

import {
  type CustomRoleTaxonomySelection,
  TaxonomyType,
} from '@revelio/data-access';

import { useActivityTaxonomyTree } from '../../../../shared/selection-lists/hooks/useActivityTaxonomyTree';
import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useGeographyTaxonomyTree } from '../../../../shared/selection-lists/hooks/useGeographyTaxonomyTree';
import { useTaxonomyTree } from '../../../../shared/selection-lists/hooks/useTaxonomyTree';
import { getRoleTaxonomyId } from '../../../../shared/selection-lists/utils/roleTaxonomy';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { FilterId, FilterType } from '../../filters/types/filters';
import { ROLE_TAXONOMY_TREE_LEVELS } from '../../filters/utils/taxonomy-tree-levels';
import type { NestableTreeNode } from '../Chart/utils/buildNestedTreemapData';
import type { TaxonomyDimension } from './taxonomy-levels';

export type TaxonomyTrees = Readonly<
  Partial<Record<TaxonomyDimension, readonly NestableTreeNode[]>>
>;

export const useTaxonomyTrees = (
  dimensions: ReadonlySet<string>
): { trees: TaxonomyTrees; isLoading: boolean } => {
  const {
    activeFilterMap,
    geographyTaxonomyId,
    skillTaxonomyId,
    skillTaxonomyVersion,
    activityTaxonomyId,
    activityTaxonomyVersion,
  } = useFilterContext();
  const roleTaxonomyFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY);
  const roleTaxonomySelection =
    roleTaxonomyFilter?.type === FilterType.VALUE
      ? (roleTaxonomyFilter.value as CustomRoleTaxonomySelection)
      : undefined;
  const roleTaxonomyId = useMemo(
    () => getRoleTaxonomyId(roleTaxonomySelection),
    [roleTaxonomySelection]
  );

  const wantsRole = dimensions.has('role');
  const wantsGeography = dimensions.has('geography');
  const wantsSkills = dimensions.has('skills');
  const wantsActivities = dimensions.has('activities');

  const { treeData: geography, fetching: isGeographyFetching } =
    useGeographyTaxonomyTree({
      taxonomyId: geographyTaxonomyId,
      pause: !wantsGeography,
    });

  const { treeData: role, fetching: isRoleFetching } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Role,
    taxonomyId: roleTaxonomyId,
    levels: ROLE_TAXONOMY_TREE_LEVELS,
    pause: !wantsRole || roleTaxonomyId === undefined,
  });

  const { treeData: skills, isCustomSkillTaxonomyPending: isSkillsFetching } =
    useCustomSkillTaxonomyTree(skillTaxonomyId, skillTaxonomyVersion, {
      enabled: wantsSkills,
    });

  const {
    treeData: activities,
    isActivityTaxonomyPending: isActivitiesFetching,
  } = useActivityTaxonomyTree({
    overrideTaxonomyId: activityTaxonomyId,
    taxonomyVersionOverride: activityTaxonomyVersion,
    enabled: wantsActivities,
  });

  const trees = useMemo<TaxonomyTrees>(
    () => ({
      ...(wantsRole ? { role } : {}),
      ...(wantsGeography ? { geography } : {}),
      ...(wantsSkills ? { skills } : {}),
      ...(wantsActivities ? { activities } : {}),
    }),
    [
      wantsRole,
      role,
      wantsGeography,
      geography,
      wantsSkills,
      skills,
      wantsActivities,
      activities,
    ]
  );

  return {
    trees,
    isLoading:
      (wantsRole && isRoleFetching) ||
      (wantsGeography && isGeographyFetching) ||
      (wantsSkills && isSkillsFetching) ||
      (wantsActivities && isActivitiesFetching),
  };
};
